import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Nav, NavItem, NavLink, Col, Tooltip, Spinner } from 'reactstrap';
import * as Icon from 'react-feather';
import { AuthContext } from '../../context/authContext';
import ComponentCard from '../../layouts/containers/ComponentCard';
import PestañasDatos from '../../components/users/PestañasDatos';
import Matriculaciones from '../../components/users/Matriculaciones';
import Ciclos from '../../components/users/Ciclos';
import PeticionMatricula from '../../components/users/PeticionMatricula';
import { SearchContext } from '../../context/searchContext';
import alumnosApiService from '../../services/http/alumnosApiService';
import matriculacionApiService from '../../services/http/matriculacionApiService';
import { getItem, setItem, removeItem, STORAGE_KEYS } from '../../services/storage';

const DetallesUsuarios = () => {
  const navigate = useNavigate();
  // Id del alumno a mostrar.
  const { studentId, empresaId } = useParams();

  // Seteamos el last update (solo al montar)
  useEffect(() => {
    const d = new Date();
    const date = `${d.getFullYear()}-${d.getMonth()}-${d.getDay()} ${d.getHours()}:${d.getMinutes()}:${d.getSeconds()}`;
    setItem(STORAGE_KEYS.LAST_UPDATE, date);
  }, []);

  // Preparamos variables
  const { userOU } = useContext(AuthContext);
  const { saveSearches, searchContext } = useContext(SearchContext);

  // Preparamos estados
  const [missingData, setMissingData] = useState(false);
  const [missingDataT, setMissingDataT] = useState(false);
  const [missingDataE, setMissingDataE] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const prevActiveTab = useRef(null);
  const [dataApi, setDataApi] = useState(null);
  const [balance, setBalance] = useState(0);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [recibosPendientes, setRecibosPendientes] = useState(false);
  const [dataCharged, setDataCharged] = useState(false);
  const [isCoursing, setIsCoursing] = useState(true);

  // Peticiones API — paralelizadas para minimizar re-renders
  useEffect(() => {
    const fetchAlumnoData = async () => {
      if ((userOU.idCedesca === -4 && userOU.idCntec === -4) || dataCharged) return;

      // Primera llamada obligatoria (necesita completarse para setItem)
      try {
        const student = await alumnosApiService.getAlumnoById(studentId, {
          headquarter: empresaId,
        });
        if (getItem(STORAGE_KEYS.STUDENT)) removeItem(STORAGE_KEYS.STUDENT);
        setItem(STORAGE_KEYS.STUDENT, student);
      } catch (error) {
        // No hacer nada específico aquí.
      }

      // Paralelizar el resto de llamadas
      const [balanceResult, coursingResult, alumnoResult] = await Promise.allSettled([
        matriculacionApiService.consultarBalanceGeneral({
          headquarter: empresaId,
          id: studentId,
        }),
        matriculacionApiService.estaCursando({
          headquarter: empresaId,
          id: studentId,
        }),
        alumnosApiService.cargarAlumno({
          id: studentId,
          headquarter: empresaId,
          idCedesca: userOU.idCedesca,
          idCntec: userOU.idCntec,
        }),
      ]);

      // Aplicar todos los estados de golpe para minimizar re-renders
      if (balanceResult.status === 'fulfilled') {
        setBalance(parseFloat(balanceResult.value.Balance));
      }
      if (coursingResult.status === 'fulfilled') {
        setIsCoursing(coursingResult.value);
      }
      if (alumnoResult.status === 'fulfilled') {
        const actualData = alumnoResult.value;
        const nullToEmpty = (v) => (v === null ? '' : v);
        setDataApi({
          ...actualData,
          datos: {
            ...actualData.datos,
            personales: {
              ...actualData.datos.personales,
              lopdSede: nullToEmpty(actualData.datos.personales.lopdSede),
              lopdComunicaciones: nullToEmpty(actualData.datos.personales.lopdComunicaciones),
              lopdFecha: nullToEmpty(actualData.datos.personales.lopdFecha),
            },
            escolares: {
              ...actualData.datos.escolares,
              idioma:
                actualData.datos.escolares.idioma === '' ||
                actualData.datos.escolares.idioma === null
                  ? 'Español'
                  : actualData.datos.escolares.idioma,
              idalu: nullToEmpty(actualData.datos.escolares.idalu),
              sede: nullToEmpty(actualData.datos.escolares.sede),
              nee: nullToEmpty(actualData.datos.escolares.nee),
              neeComment: nullToEmpty(actualData.datos.escolares.neeComment),
              tsi: nullToEmpty(actualData.datos.escolares.tsi),
              situacionLaboral: nullToEmpty(actualData.datos.escolares.situacionLaboral),
              formacionAcademica: nullToEmpty(actualData.datos.escolares.formacionAcademica),
              antiguoCentro: nullToEmpty(actualData.datos.escolares.antiguoCentro),
              antiguosEstudios: nullToEmpty(actualData.datos.escolares.antiguosEstudios),
              notaMedia: nullToEmpty(actualData.datos.escolares.notaMedia),
              siguientesEstudios: nullToEmpty(actualData.datos.escolares.siguientesEstudios),
              formaAcceso: nullToEmpty(actualData.datos.escolares.formaAcceso),
              emailCorp: nullToEmpty(actualData.datos.escolares.emailCorp),
              fechaUltimoCurso: nullToEmpty(actualData.datos.escolares.fechaUltimoCurso),
            },
          },
        });
      }
      setDataCharged(true);
    };

    fetchAlumnoData();
  }, [dataCharged, empresaId, studentId, userOU.idCedesca, userOU.idCntec]);

  // Actualizar título de la pestaña
  useEffect(() => {
    if (dataApi && dataApi.datos && dataApi.datos.personales) {
      const { nombre, apellidos } = dataApi.datos.personales;
      document.title = `Admin - ${nombre} ${apellidos}`;
    }

    // Limpiar el título cuando se desmonte el componente
    return () => {
      document.title = 'Admin';
    };
  }, [dataApi]);

  // Funciones
  const toggle = (tab) => {
    if (activeTab !== tab) {
      prevActiveTab.current = activeTab;
      setActiveTab(tab);
    }
  };

  const handleOnClick = () => {
    const search = searchContext;
    search.newSearch = false;
    saveSearches(search);
    navigate('/alumnos');
  };

  const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

  if (!dataCharged)
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
      </div>
    );

  return (
    <>
      <Col md="12">
        <ComponentCard
          title="Gestión de alumnos"
          dropdown
          handleClick={handleOnClick}
          hasButton={false}
          isProfile
        >
          <Nav tabs>
            <NavItem>
              <NavLink
                className={activeTab === '1' ? 'active' : ''}
                onClick={() => {
                  toggle('1');
                }}
              >
                Datos personales
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === '2' ? 'active' : ''}
                onClick={() => {
                  toggle('2');
                }}
              >
                Datos escolares
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === '3' ? 'active' : ''}
                onClick={() => {
                  toggle('3');
                }}
              >
                Datos tutores
              </NavLink>
            </NavItem>
            {dataApi !== null &&
            dataApi.datos.personales.driveId !== null &&
            dataApi.datos.personales.driveId !== '' ? (
              <NavItem>
                <NavLink
                  className={activeTab === '4' ? 'active' : ''}
                  onClick={() => {
                    toggle('4');
                  }}
                >
                  Documentación
                </NavLink>
              </NavItem>
            ) : (
              ''
            )}
            {userOU.centro === 'SUPERADMIN' ||
            userOU.centro === 'CNTEC Girona' ||
            userOU.centro === 'CEDESCA' ||
            userOU.centro === 'CNTEC Sevilla' ||
            userOU.centro === 'SEDE' ||
            userOU.centro === 'SECRETARÍA' ||
            userOU.centro === 'ORIENTACIÓN' ? (
              <NavItem>
                <NavLink
                  className={activeTab === '5' ? 'active' : ''}
                  onClick={() => {
                    toggle('5');
                  }}
                >
                  SEPA
                </NavLink>
              </NavItem>
            ) : (
              ''
            )}
            {userOU.centro === 'SUPERADMIN' ||
            userOU.centro === 'CNTEC Girona' ||
            userOU.centro === 'CEDESCA' ||
            userOU.centro === 'CNTEC Sevilla' ||
            userOU.centro === 'SEDE' ||
            userOU.centro === 'SECRETARÍA' ||
            userOU.centro === 'ORIENTACIÓN' ? (
              <NavItem>
                <NavLink
                  className={activeTab === '6' ? 'active' : ''}
                  onClick={() => {
                    toggle('6');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  Contabilidad{' '}
                  {recibosPendientes && <Icon.AlertCircle id="Tooltip" color="red" size={22} />}
                  {recibosPendientes && (
                    <Tooltip
                      placement="top"
                      isOpen={tooltipOpen}
                      target="Tooltip"
                      toggle={toggleTooltip}
                    >
                      Este alumno tiene recibos pendientes
                    </Tooltip>
                  )}
                </NavLink>
              </NavItem>
            ) : (
              ''
            )}
            {userOU.centro === 'SUPERADMIN' ||
            userOU.centro === 'CNTEC Girona' ||
            userOU.centro === 'CEDESCA' ||
            userOU.centro === 'CNTEC Sevilla' ||
            userOU.centro === 'SEDE' ||
            userOU.centro === 'SECRETARÍA' ||
            userOU.centro === 'ORIENTACIÓN' ? (
              <NavItem>
                <NavLink
                  className={activeTab === '7' ? 'active' : ''}
                  onClick={() => {
                    toggle('7');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  Seguimiento
                </NavLink>
              </NavItem>
            ) : (
              ''
            )}
            <NavItem>
              <NavLink
                className={activeTab === '8' ? 'active' : ''}
                onClick={() => {
                  toggle('8');
                }}
              >
                Requerimientos
              </NavLink>
            </NavItem>
          </Nav>
          <PestañasDatos
            setRecibosPendientes={setRecibosPendientes}
            prevActiveTab={prevActiveTab.current}
            activeTab={activeTab}
            dataApi={dataApi}
            missingDataFunction={setMissingData}
            missingDataFunctionE={setMissingDataE}
            missingDataFunctionT={setMissingDataT}
          />
        </ComponentCard>
        {dataApi !== null ? <Matriculaciones matriculas={dataApi.matriculas} /> : ''}
        {dataApi !== null ? (
          <PeticionMatricula
            recibosPendientes={recibosPendientes}
            missingData={missingData}
            missingDataE={missingDataE}
            missingDataT={missingDataT}
            isCoursing={isCoursing}
          />
        ) : (
          ''
        )}
        {dataApi !== null && dataApi.ciclos.length > 0 ? (
          <Ciclos
            recibosPendientes={recibosPendientes}
            ciclos={dataApi.ciclos}
            datos={dataApi.datos}
            matriculas={dataApi.matriculas}
            balance={balance}
          />
        ) : (
          ''
        )}
      </Col>
    </>
  );
};

export default DetallesUsuarios;
